<template>
  <div class="license-page">
    <div class="page-head">
      <div>
        <div class="page-title">{{ isEdit ? '编辑套餐' : '新增套餐' }}</div>
        <div class="page-subtitle">保存时只提交 feature_keys，服务端负责转换成套餐关系。</div>
      </div>
      <div class="head-actions">
        <el-button icon="el-icon-back" @click="goBack">返回</el-button>
        <el-button type="primary" icon="el-icon-check" :loading="saving" @click="savePlan">保存</el-button>
      </div>
    </div>

    <div v-loading="loading" class="edit-grid">
      <section class="panel">
        <div class="panel-title">基础信息</div>
        <el-form :model="form" label-width="100px">
          <el-form-item label="套餐 key">
            <el-input v-model="form.plan_key" placeholder="例如 standard / pro" />
          </el-form-item>
          <el-form-item label="套餐名称">
            <el-input v-model="form.plan_name" placeholder="例如 Pro会员" />
          </el-form-item>
          <el-form-item label="启用状态">
            <el-switch v-model="form.enabled" :active-value="1" :inactive-value="0" />
          </el-form-item>
          <el-form-item label="系统套餐">
            <el-switch v-model="form.is_system" :active-value="1" :inactive-value="0" />
          </el-form-item>
          <el-form-item label="排序">
            <el-input-number v-model="form.sort" :min="0" :max="999999" />
          </el-form-item>
          <el-form-item label="说明">
            <el-input v-model="form.description" type="textarea" :autosize="{ minRows: 4, maxRows: 7 }" />
          </el-form-item>
        </el-form>
      </section>

      <section class="panel">
        <div class="panel-title-row">
          <div>
            <div class="panel-title">功能勾选</div>
            <div class="panel-subtitle">已选 {{ selectedFeatureKeys.length }} 项</div>
          </div>
          <div>
            <el-button size="mini" @click="selectAllFeatures">全选</el-button>
            <el-button size="mini" @click="clearFeatures">清空</el-button>
          </div>
        </div>

        <el-input v-model="featureKeyword" clearable placeholder="过滤 feature key / 名称" class="feature-filter" />

        <div class="feature-groups">
          <div v-for="group in groupedFeatures" :key="group.category" class="feature-group">
            <div class="feature-group-title">{{ group.category || '未分类' }}</div>
            <el-checkbox-group v-model="selectedFeatureKeys">
              <el-checkbox
                v-for="feature in group.items"
                :key="feature.feature_key"
                :label="feature.feature_key"
                class="feature-check"
              >
                <span class="feature-name">{{ feature.feature_name }}</span>
                <span class="feature-key">{{ feature.feature_key }}</span>
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import apis from '@/api/index'

export default {
  name: 'LicensePlanEdit',
  data() {
    return {
      loading: false,
      saving: false,
      featureKeyword: '',
      form: this.createEmptyForm(),
      allFeatures: [],
      selectedFeatureKeys: [],
    }
  },
  computed: {
    isEdit() {
      return Number(this.form.id || 0) > 0
    },
    // 按分类聚合 feature，减少长列表扫描成本。
    groupedFeatures() {
      const keyword = String(this.featureKeyword || '').toLowerCase()
      const groups = {}
      this.allFeatures.forEach((feature) => {
        const text = (String(feature.feature_key || '') + ' ' + String(feature.feature_name || '')).toLowerCase()
        if (keyword && text.indexOf(keyword) === -1) return
        const category = feature.category || 'other'
        if (!groups[category]) groups[category] = []
        groups[category].push(feature)
      })
      return Object.keys(groups).sort().map((category) => {
        return { category, items: groups[category] }
      })
    },
  },
  methods: {
    // 创建空套餐表单，新增和读取失败时都使用这份安全默认值。
    createEmptyForm() {
      return {
        id: 0,
        plan_key: '',
        plan_name: '',
        description: '',
        enabled: 1,
        is_system: 0,
        sort: 0,
      }
    },
    // 读取套餐详情和全部可选 feature。
    readDetail() {
      this.loading = true
      const id = Number(this.$route.query.id || 0)
      apis.license.readPlan({ id }).then((res) => {
        const data = res.data && res.data.data ? res.data.data : {}
        this.form = Object.assign(this.createEmptyForm(), data.plan || {})
        this.form.enabled = Number(this.form.enabled || 0) > 0 ? 1 : 0
        this.form.is_system = Number(this.form.is_system || 0) > 0 ? 1 : 0
        this.form.sort = Number(this.form.sort || 0)
        this.allFeatures = data.features || []
        this.selectedFeatureKeys = data.feature_keys || []
      }).catch((err) => {
        this.$message({ message: String(err || '读取套餐失败'), type: 'error' })
      }).finally(() => {
        this.loading = false
      })
    },
    // 选择当前全部启用 feature，适合快速创建 pro 类套餐。
    selectAllFeatures() {
      this.selectedFeatureKeys = this.allFeatures.map((feature) => feature.feature_key)
    },
    // 清空勾选，方便重新配置小套餐。
    clearFeatures() {
      this.selectedFeatureKeys = []
    },
    // 保存套餐基础信息和 feature_keys。
    savePlan() {
      const payload = Object.assign({}, this.form, {
        feature_keys: this.selectedFeatureKeys,
      })
      this.saving = true
      apis.license.savePlan(payload).then((res) => {
        const body = res.data || {}
        if (body.code !== 1) {
          this.$message({ message: body.msg || '保存失败', type: 'error' })
          return
        }
        this.$message({ message: '保存成功', type: 'success' })
        const id = body.data && body.data.id ? body.data.id : this.form.id
        this.$router.replace({ name: 'licensePlanEdit', query: { id } })
        this.readDetail()
      }).catch((err) => {
        this.$message({ message: String(err || '保存失败'), type: 'error' })
      }).finally(() => {
        this.saving = false
      })
    },
    // 返回套餐列表。
    goBack() {
      this.$router.push({ name: 'licensePlans' })
    },
  },
  mounted() {
    this.readDetail()
  },
}
</script>

<style lang="scss" scoped>
.license-page {
  padding: 18px;
  background: #f5f7fa;
  min-height: calc(100vh - 36px);
}
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.page-title {
  font-size: 24px;
  color: #1f2d3d;
  line-height: 32px;
}
.page-subtitle,
.panel-subtitle {
  color: #6b7280;
  font-size: 13px;
  margin-top: 4px;
}
.head-actions {
  display: flex;
  gap: 8px;
}
.edit-grid {
  display: grid;
  grid-template-columns: minmax(360px, 440px) 1fr;
  gap: 16px;
}
.panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 18px;
}
.panel-title,
.feature-group-title {
  font-size: 16px;
  color: #1f2d3d;
  margin-bottom: 14px;
}
.panel-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.feature-filter {
  margin-bottom: 12px;
}
.feature-groups {
  max-height: calc(100vh - 220px);
  overflow: auto;
  padding-right: 6px;
}
.feature-group {
  border-top: 1px solid #ebeef5;
  padding-top: 12px;
  margin-top: 12px;
}
.feature-check {
  display: block;
  margin: 0 0 10px 0;
  line-height: 22px;
}
.feature-name {
  color: #303133;
  margin-right: 8px;
}
.feature-key {
  color: #909399;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}
</style>
